import { useSelector } from "react-redux";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { getUrlForImage } from "@CubeComponents/constants/globalFunctions";
import { convertDate } from "@Services/luxonHelper";
import React, { useEffect, useRef, useState } from "react";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import _ from "lodash";
import { useTranslation } from "@i18n";
import { getImageUrl } from "@Utils";
import IconComponent from "@CubeComponents/icons";
import Text from "@CubeComponents/html/Text/Text";
import CheckBoxComponent from "../../../../cubeComponent/Mui_Form/CheckBox";
import { RootState } from "@Reducers/index";
import type { IssueItem } from "@ServerTypes/issues/issues.schema";
import IssueStatusPopover from "../IssueStatusPopover/IssueStatusPopover";
import { isIssueRequiredPermission } from "@Features/issues/state/bcfData.actions";
import { folderPermissions } from "@Helpers/constants";

const { RESOURCE_COMMENT } = folderPermissions();

interface IssueCardProps {
  showAnimation: boolean;
  currentViewIssueId: string;
  onViewIssueClick: (issueId: string) => void;
  handleSelectedIssues: (issueId: string, deleteAll?: boolean) => void;
  selectedIssues: Record<string, boolean>;
}

// A card always renders a fully-loaded issue from the `issues` store; these fields are
// always present on a stored issue (the server `IssueItem` types them optional because the
// list/create/detail shapes share one type). `createdBy` is read here purely as a user-id
// string passed to `UserNameTagComponent`.
type LoadedIssue = IssueItem & {
  id: string;
  title: string;
  issueCounter: string | number;
  createdBy: string;
  createdDate: number;
};

function IssueCard({
  showAnimation,
  currentViewIssueId,
  onViewIssueClick,
  handleSelectedIssues,
  selectedIssues,
}: IssueCardProps) {
  const { t } = useTranslation();
  const imageRef = useRef<HTMLImageElement>(null);
  const { issues } = useSelector((state: RootState) => state);
  const flagForAnimation = useSelector((state: RootState) => state.HoopsModalStatus?.Issues?.flagForAnimation);
  const [coords, setCoords] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });

  useEffect(() => {
    const targetImage = imageRef.current;
    if (showAnimation && targetImage) {
      const checkTargetImage = () => {
        const rect = targetImage.getBoundingClientRect();
        if (rect) {
          setCoords({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          });
        }
      };

      checkTargetImage();
    }
  }, [showAnimation]);
  const [imageDataForIssue, setImageDataForIssue] = useState(getUrlForImage({ id: currentViewIssueId }, "IssueImage"));

  useEffect(() => {
    if (_.get(issues, `[${currentViewIssueId}].snapshot`)) {
      setImageDataForIssue(getUrlForImage({ id: currentViewIssueId }, "IssueImage"));
    }
  }, [issues, currentViewIssueId]);

  const handleSelectIssueCheckbox = (_event: unknown, issueId: string) => {
    handleSelectedIssues(issueId);
  };

  const handleViewIssueClick = (params: string) => {
    onViewIssueClick(params);
  };

  const handleImageError = () => {
    setImageDataForIssue(`${getImageUrl()}/Issues_default_image.svg`);
  };

  const getUserAvatar = (id: string) => {
    return <UserNameTagComponent id={id} bgColor="var(--system-colour-4)" size="SM" />;
  };

  const issueHtml = () => {
    const Issues = issues[currentViewIssueId] as LoadedIssue;

    return (
      <div
        className="group bg-[linear-gradient(116deg,_#fff_45%,_#f0f5f9_129%)] rounded shadow-[0.25rem_0.25rem_0.9375rem_0_rgb(65_92_171_/_15%)] p-4 border border-[#f0f5f9] border-s-1 flex flex-col gap-4 flex-shrink-2 hover:shadow-[0.625rem_0.625rem_0.9375rem_0_rgb(0_0_0_/_20%)]"
        style={{
          borderInlineStart: Issues.issueType === "external" ? "solid 0.3125rem #ffcf55" : "solid 0.3125rem #cae4f0",
        }}
        key={`issue_${Issues.issueCounter}`}
        onClick={() => handleViewIssueClick(Issues.id)}
      >
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3 min-h-5">
            <span className="flex items-center" data-testid="issuesCheckBoxSpan">
              <IconComponent
                className={`h-4 w-4 text-text_light
                  ${Object.keys(selectedIssues).length !== 0 ? "hidden" : ""}
                  group-hover:hidden
                `}
                name="bx_hash"
                width="1rem"
                height="1rem"
              />
              <CheckBoxComponent
                onChange={(event) => handleSelectIssueCheckbox(event, Issues.id)}
                className={
                  Object.keys(selectedIssues).length !== 0
                    ? "block visible"
                    : "hidden invisible group-hover:block group-hover:visible"
                }
                sx={{ padding: 0 }}
                onClick={(e) => e.stopPropagation()}
              />
            </span>

            <span data-testid="issuesProjectId">
              <div className="flex items-center gap-2">
                {Issues.issueCounter}
                {!Issues.isFormTemplateValid && (
                  <IconComponent
                    name="ErrorIcon"
                    toolTipContent={t("Mandatory custom fields not filled.")}
                    position="bottom"
                    width="1rem"
                    height="1rem"
                  />
                )}
              </div>
            </span>
          </div>
          <div
            className="flex flex-col justify-center gap-2 text-xs text-[var(--primary-text)] min-h-5 me-4"
            data-testid="issuesHeaderDate"
          >
            <div className="flex gap-5 justify-between text-[#666a84]" data-testid="createdOnDateIssues">
              <Text data-testid="createdOnTextIssues">{t("Created On:-")}</Text>
              <span>
                <ToolTipComponent content={convertDate(Number(Issues.createdDate), "dateForTooltip")} position="bottom">
                  <div className="text-xs">{convertDate(Number(Issues.createdDate), "dateForText")}</div>
                </ToolTipComponent>
              </span>
            </div>
            {Issues.dueDate && (
              <div className="flex gap-5 justify-between text-[#666a84]" data-testid="dueDateIssues">
                <Text data-testid="dueDateTextIssues">{t("Due Date:-")}</Text>
                <span>
                  <ToolTipComponent content={convertDate(Number(Issues.dueDate), "dateForTooltip")} position="bottom">
                    <div className="text-xs">{convertDate(Number(Issues.dueDate), "dateForText")}</div>
                  </ToolTipComponent>
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="text-sm font-semibold text-[var(--primary-text)] me-4" data-testid="cardTitleIssues">
          <span className="font-semibold" data-testid="cardTitleTextIssues">
            {Issues.title}
          </span>
        </div>
        <div className="flex flex-row items-center w-full gap-[1.375rem] cursor-pointer mt-1">
          <div className="flex flex-col items-center gap-2.5" data-testid="cardImgIssues">
            <span className="w-full">
              <img
                ref={imageRef}
                className="h-[6.25rem] w-[9.375rem] flex-grow-0 rounded-md shadow-[0_0_0.25rem_0_rgb(0_0_0_/_15%)] m-0 object-contain"
                src={imageDataForIssue}
                alt="animation_image"
                onError={handleImageError}
              />

              {showAnimation &&
                flagForAnimation &&
                (() => {
                  // Animation target uses live mouse-rect coordinates (getBoundingClientRect)
                  // which are CSS pixels — px is the correct unit here, not rem.
                  const convergeStyle = {
                    "--converge-x": `${coords.left}px`,
                    "--converge-y": `${coords.top}px`,
                    "--final-width": `${coords.width}px`,
                    "--final-height": `${coords.height}px`,
                  } as React.CSSProperties;
                  return (
                    <img
                      className="z-50 fixed animate-converge"
                      style={convergeStyle}
                      src={getUrlForImage({ id: Issues.id }, "IssueImage")}
                      alt="Full screen background"
                    />
                  );
                })()}
            </span>
          </div>
          <div className="flex flex-col px-2 gap-4 w-full text-sm">
            <div className="flex gap-4 items-center">
              <Text className="w-20 font-semibold text-text_light">{t("Assigned to")}</Text>
              {Issues.assignedTo?.length ? (
                <UserNameTagComponent
                  showAvatarOnly={Issues.assignedTo.length != 1}
                  userIdsForGroup={Issues.assignedTo.length == 1 ? undefined : Issues.assignedTo.map((u) => u._id)}
                  id={Issues.assignedTo.length == 1 ? Issues.assignedTo[0]?._id : undefined}
                  maxAvatars={Issues.assignedTo.length == 1 ? undefined : 5}
                  size="XXS"
                  isCircle
                  enableDetailsModalForGroupedUsers
                  detailsModalHeader={t("Assigned To")}
                />
              ) : (
                <ToolTipComponent content={t("Unassigned")} position="bottom">
                  <img src="/assets/icons/svg/Disabled User.svg" className="w-5 h-5" alt="disabled_user" />
                </ToolTipComponent>
              )}
            </div>
            <div className="flex gap-4 items-center">
              <Text className="w-20 font-semibold text-text_light">{t("Created by")}</Text>
              {getUserAvatar(Issues.createdBy)}
            </div>
            <div className="flex gap-4 items-center">
              <Text className="w-20 font-semibold text-text_light">{t("Status")}</Text>
              <IssueStatusPopover
                issue={{
                  id: Issues.id,
                  spaceId: Issues.spaceId ?? "",
                  fileVersionId: Issues.fileVersionId,
                  statusInReview: Issues.statusInReview,
                  reviews: Issues.reviews,
                  canEditDefaultStatus: isIssueRequiredPermission(Issues, RESOURCE_COMMENT.id),
                }}
                canEdit
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return <div className="w-full">{issues[currentViewIssueId] ? issueHtml() : null}</div>;
}

export default IssueCard;
